import React, { memo } from 'react'
import { HighlightedText } from './HighlightedText'
import { PathItem } from './PathItem'
import { Link } from '@tanstack/react-router'
import { useInView } from 'react-intersection-observer'
import { useGetDocumentTree } from '@/shared/hooks/search/useGetDocumentTree'
import { Skeleton } from '@workspace/ui/components/Skeleton'
import { useGetAttachmentTree } from '@/shared/hooks/search/useGetAttachmentTree'

interface DocumentItemProps {
    title: string
    id: string
    code?: string | null
    projectId?: string | null
    description?: string | null
    documentId?: string | null
    highlights?: {
        title?: string[]
        description?: string[]
        content?: string[]
    }
    onClick?: () => void
}

export const DocumentItem = memo(
    ({ title, code, projectId, description, id, highlights, onClick }: DocumentItemProps) => {
        const { ref, inView } = useInView({
            threshold: 0,
            triggerOnce: true,
        })
        const { data, isLoading } = useGetDocumentTree(id, inView)

        const lastParentFolder = !!data?.parentFolders?.length && {
            name: data?.parentFolders?.at(-1)?.name || '',
            level: data?.parentFolders?.length || 0,
        }

        const innerContent = (
            <>
                <img src="/svg/document.svg" alt="Project" className="size-5" />

                <div className="flex flex-col min-w-0 flex-1 gap-2">
                    <div className="flex items-center justify-between gap-2">
                        <HighlightedText
                            text={title}
                            highlight={highlights?.title}
                            className="text-sm font-medium text-foreground truncate leading-5 group-hover:underline"
                        />

                        <HighlightedText
                            text={code}
                            className="text-sm font-medium text-foreground truncate leading-5"
                        />
                    </div>

                    {(highlights?.description || highlights?.content) && (
                        <HighlightedText
                            text={description}
                            highlight={highlights?.description || highlights?.content}
                            className="text-xs text-neutral-500"
                        />
                    )}

                    {isLoading ? (
                        <Skeleton className="h-4 w-48" />
                    ) : (
                        <PathItem
                            parentFolder={
                                lastParentFolder
                                    ? { name: lastParentFolder?.name || '', level: lastParentFolder?.level || 0 }
                                    : undefined
                            }
                            projectName={data?.project?.name || ''}
                        />
                    )}
                </div>
            </>
        )

        if (projectId) {
            return (
                <Link
                    ref={ref}
                    to="/projects/$id/project-profile"
                    params={{
                        id: projectId,
                    }}
                    search={{
                        searchId: id,
                        type: 'DOCUMENT',
                    }}
                    className="group flex items-start gap-3 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-800 rounded-md px-2"
                    onClick={onClick}
                >
                    {innerContent}
                </Link>
            )
        }

        return (
            <Link
                ref={ref}
                to="/documents"
                search={{
                    searchId: id,
                    type: 'DOCUMENT',
                }}
                className="group flex items-start gap-3 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-800 rounded-md px-2"
                onClick={onClick}
            >
                {innerContent}
            </Link>
        )
    },
)

DocumentItem.displayName = 'DocumentItem'

interface DocumentContentItemProps extends DocumentItemProps {
    content?: string | null
}

export const DocumentContentItem = memo(
    ({ title, code, projectId, content, id, highlights, onClick, documentId }: DocumentContentItemProps) => {
        const normalizedData = [highlights?.content?.join(' ... ') || '']
        const { ref, inView } = useInView({
            threshold: 0,
            triggerOnce: true,
        })
        const { data, isLoading } = useGetAttachmentTree(id, inView)

        const lastParentFolder = !!data?.parentFolders?.length && {
            name: data?.parentFolders?.at(-1)?.name || '',
            level: data?.parentFolders?.length || 0,
        }

        const innerContent = (
            <>
                <img src="/svg/document.svg" alt="Project" className="size-5" />

                <div className="flex flex-col min-w-0 flex-1 gap-2">
                    <div className="flex items-center justify-between gap-2">
                        <HighlightedText
                            text={title}
                            highlight={highlights?.title}
                            className="text-sm font-medium text-foreground truncate leading-5 group-hover:underline"
                        />

                        <HighlightedText
                            text={code}
                            className="text-sm font-medium text-foreground truncate leading-5"
                        />
                    </div>

                    <HighlightedText text={content} highlight={normalizedData} className="text-xs text-neutral-500" />

                    {isLoading ? (
                        <Skeleton className="h-4 w-48" />
                    ) : (
                        <PathItem
                            parentFolder={
                                lastParentFolder
                                    ? { name: lastParentFolder?.name || '', level: lastParentFolder?.level || 0 }
                                    : undefined
                            }
                            projectName={data?.project?.name || ''}
                        />
                    )}
                </div>
            </>
        )

        if (projectId) {
            return (
                <Link
                    ref={ref}
                    to="/projects/$id/project-profile"
                    params={{
                        id: projectId,
                    }}
                    search={{
                        searchId: id,
                        type: 'DOCUMENT_CONTENT',
                        documentId: documentId || '',
                    }}
                    className="group flex items-start gap-3 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-800 rounded-md px-2"
                    onClick={onClick}
                >
                    {innerContent}
                </Link>
            )
        }

        return (
            <Link
                ref={ref}
                to="/documents"
                search={{
                    searchId: id,
                    type: 'DOCUMENT_CONTENT',
                    documentId: documentId || '',
                }}
                className="group flex items-start gap-3 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-800 rounded-md px-2"
                onClick={onClick}
            >
                {innerContent}
            </Link>
        )
    },
)

DocumentContentItem.displayName = 'DocumentContentItem'
